'use client'

import { useEffect, useRef, useState } from 'react'
import QRCode from 'react-qr-code'
import { useSearchParams } from 'next/navigation'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import {
	TelegramAuthService,
	type TelegramLoginRequest,
} from '@/services/telegram-auth.service'

const POLL_INTERVAL_MS = 2000

/**
 * Сценарий C бэкенд-гайда: заявка на сайте → число подтверждается в боте →
 * сайт узнаёт результат поллингом. Число — не формальность (анти-фишинг,
 * см. skuf-bot/README.md): без него голая кнопка "Войти" в боте подтверждала
 * бы чужой вход, если ссылку перехватили — поэтому его нельзя убирать ради
 * упрощения UX.
 */
export function TelegramLoginButton() {
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [request, setRequest] = useState<TelegramLoginRequest | null>(null)
	const [expired, setExpired] = useState(false)
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

	const searchParams = useSearchParams()
	const productId = searchParams.get('product_id')

	function stopPolling() {
		if (pollRef.current) {
			clearInterval(pollRef.current)
			pollRef.current = null
		}
	}
	useEffect(() => stopPolling, [])

	async function start() {
		setLoading(true)
		setExpired(false)
		try {
			const data = await TelegramAuthService.loginRequest()
			setRequest(data)
			setOpen(true)

			pollRef.current = setInterval(async () => {
				try {
					const result = await TelegramAuthService.loginPoll(data.poll_token)
					if (result.status === 'confirmed') {
						stopPolling()
						toast.success('Вход выполнен успешно!')
						// Не разблокируем диалог — через секунду редирект.
						setTimeout(() => {
							window.location.href = productId
								? `/profile/subscriptions/subscribe?product_id=${productId}`
								: '/profile'
						}, 800)
					} else if (result.status === 'expired') {
						stopPolling()
						setExpired(true)
					}
				} catch (error: any) {
					stopPolling()
					setOpen(false)
					toast.error(error.response?.data?.message || 'Ошибка входа через Telegram')
				}
			}, POLL_INTERVAL_MS)
		} catch (error: any) {
			toast.error(
				error.response?.data?.message || 'Не удалось начать вход через Telegram'
			)
		} finally {
			setLoading(false)
		}
	}

	return (
		<>
			<Button
				type='button'
				variant='outline'
				disabled={loading}
				onClick={start}
				className='w-full bg-transparent border-gray-700 text-white hover:bg-gray-800 hover:text-white'
			>
				{loading && <Loader2 className='animate-spin' />}
				Войти через Telegram
			</Button>

			<Dialog
				open={open}
				onOpenChange={o => {
					setOpen(o)
					if (!o) stopPolling()
				}}
			>
				<DialogContent className='bg-gray-800 border-gray-700 text-center'>
					<DialogHeader>
						<DialogTitle className='text-white'>Подтвердите вход в Telegram</DialogTitle>
						<DialogDescription className='text-gray-300'>
							{expired
								? 'Время вышло, либо было выбрано неверное число — попробуйте ещё раз.'
								: 'Откройте бота и нажмите число, которое видите здесь:'}
						</DialogDescription>
					</DialogHeader>

					{!expired && request && (
						<div className='flex flex-col items-center gap-4 py-2'>
							<div className='text-5xl font-bold text-[#bfff01]'>
								{request.confirm_number}
							</div>
							<Button
								asChild
								className='bg-[#bfff01] text-black hover:bg-[#9fdf00] rounded-full px-8 font-semibold'
							>
								<a href={request.bot_deep_link} target='_blank' rel='noreferrer'>
									Открыть бота
								</a>
							</Button>
							<div className='bg-white p-3 rounded-lg'>
								<QRCode value={request.bot_deep_link} size={160} className='h-auto w-[160px]' />
							</div>
							<p className='text-xs text-gray-400'>Или отсканируйте QR с телефона</p>
						</div>
					)}

					{expired && (
						<Button
							onClick={start}
							disabled={loading}
							className='bg-[#bfff01] text-black hover:bg-[#9fdf00] rounded-full mx-auto'
						>
							{loading && <Loader2 className='animate-spin' />}
							Попробовать снова
						</Button>
					)}
				</DialogContent>
			</Dialog>
		</>
	)
}
